import { ASButton } from '@eleventheye/asui';
import ASBox from 'components/asbox/ASBox';
import Carousel from 'components/carousel';
import React, { CSSProperties, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'routes/Routes';
import styles from './Home.module.scss';

type HomeProps = {
  className?: string;
};

const showcases = [
  {
    title: 'Every matchup has a story.',
    description: 'Scores, recent form, head-to-head history, trends and predictions—all within your weekly schedule.',
    image: '/images/weekly-cards-2080x1000.webp',
    width: 2080,
    height: 1000,
    alt: 'Six weekly game cards showing predictions, trends, scores, head-to-head history, matchup rankings and recent form.',
  },
  {
    title: 'See how teams stack up.',
    description:
      'Compare performances side by side, with weekly and season rankings putting the numbers in perspective.',
    image: '/images/team-comparison-v1.webp',
    width: 1826,
    height: 1000,
    alt: 'Patriots and Raiders offensive statistics compared with horizontal bars, weekly rankings and season rankings.',
  },
  {
    title: 'See where strengths meet.',
    description: 'Explore how one team’s offense stacks up against the other’s defense.',
    image: '/images/team-comparison-v2.webp',
    width: 1424,
    height: 1000,
    alt: 'Patriots offense and Raiders defense compared across scoring, yards, touchdowns and interceptions.',
  },
];

const widestShowcaseRatio = Math.max(...showcases.map(({ width, height }) => width / height));

/**
 * Presents the public NFL Stats showcase and account links without loading game data.
 * Slides rotate every five seconds unless paused, hovered, focused, manually selected,
 * or the visitor prefers reduced motion. Account actions stay outside the carousel.
 *
 * @param {HomeProps} props - Optional class name for the scene container.
 *
 * @return {React.ReactElement} Landing scene with a screenshot carousel and account navigation.
 */
const Home: React.FC<HomeProps> = ({ className = '' }) => {
  const [introComplete, setIntroComplete] = useState(false);

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const skipIntroduction = () => {
      if (preference.matches) setIntroComplete(true);
    };
    skipIntroduction();
    preference.addEventListener('change', skipIntroduction);
    return () => preference.removeEventListener('change', skipIntroduction);
  }, []);

  const registerPath = `${AppRoutes.Auth}/${AppRoutes.Register}`;
  const loginPath = `${AppRoutes.Auth}/${AppRoutes.Login}`;

  return (
    <ASBox
      className={`${styles.HomeContainer} ${className}`}
      style={{ '--showcase-ratio': widestShowcaseRatio } as CSSProperties}
      noMinWidth
    >
      <div className={styles.HomeHelmet} aria-hidden="true">
        <img
          className={styles.HomeHelmetImage}
          src="/images/nfl/logos/app/nflstats-helmet-logo-1000x1000.webp"
          width={1000}
          height={1000}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      </div>
      <header className={styles.HomeIntroduction}>
        <div>
          <h1 className={styles.HomeTitle}>See the game beyond the score.</h1>
          <p className={styles.HomeDescription}>
            Explore NFL stats visually, compare teams, and follow how predictions play out.
          </p>
        </div>
      </header>

      <div
        className={styles.HomeCarousel}
        inert={!introComplete}
        aria-hidden={!introComplete}
        onAnimationEnd={(event) => {
          if (event.target === event.currentTarget) setIntroComplete(true);
        }}
      >
        <Carousel
          className={styles.HomeCarouselContent}
          rotationPaused={!introComplete}
          ariaLabel="Explore NFL Stats"
          motionOpacity={0.4}
          autoPlay
          items={showcases.map((showcase) => ({
            id: showcase.image,
            label: showcase.title,
            content: (
              <div className={styles.HomeSlide}>
                <div className={styles.HomeSectionCopy}>
                  <h2 className={styles.HomeSectionTitle}>{showcase.title}</h2>
                  <p className={styles.HomeDescription}>{showcase.description}</p>
                </div>
                <div className={styles.HomeImageViewport}>
                  <img
                    className={styles.HomeImage}
                    src={showcase.image}
                    width={showcase.width}
                    height={showcase.height}
                    alt={showcase.alt}
                    decoding="async"
                    draggable={false}
                  />
                </div>
              </div>
            ),
          }))}
        />
      </div>

      <footer className={styles.HomeClosing}>
        <div className={styles.HomeActions}>
          <ASButton as={Link} to={registerPath}>
            Create account
          </ASButton>
          <Link className={styles.HomeLink} to={loginPath}>
            Log in
          </Link>
        </div>
        <p className={styles.HomeNote}>
          An account is required to explore NFL Stats.{' '}
          <Link className={styles.HomeLink} to={`${AppRoutes.Auth}/${AppRoutes.Forgot}`}>
            Forgot password?
          </Link>
        </p>
      </footer>
    </ASBox>
  );
};

export default Home;
